<script lang="ts">
  import type { FullAutoFill } from 'svelte/elements';
  import { twMerge } from 'tailwind-merge';
  import Icon from '../Icon.svelte';
  import Spinner from '../Spinner.svelte';

  type InputEvt<E extends Event> = E & {
    currentTarget: EventTarget & HTMLInputElement;
  };

  interface Props {
    value?: any;
    label?: string | null;
    placeholder?: string;
    type?: string | null;
    id?: string | null;
    name?: string | null;
    valid?: boolean;
    disabled?: boolean;
    loading?: boolean;
    accept?: string | null;
    icon?: any;
    variant?: string;
    autocomplete?: FullAutoFill;
    autofocus?: any;
    dataTestId?: string;
    min?: string | null;
    disableScrollChange?: boolean;
    class?: string;
    onchange?: (event: InputEvt<Event>) => void;
    onclick?: (event: InputEvt<MouseEvent>) => void;
    onfocus?: (event: InputEvt<FocusEvent>) => void;
    oninput?: (event: InputEvt<Event>) => void;
    onblur?: (event: InputEvt<FocusEvent>) => void;
    onkeyup?: (event: InputEvt<KeyboardEvent>) => void;
    onsearch?: (event: InputEvt<Event>) => void;
  }

  let {
    value = $bindable(),
    label = null,
    placeholder = '',
    type = null,
    id = null,
    name = null,
    valid = $bindable(true),
    disabled = false,
    loading = false,
    accept = null,
    icon = null,
    variant = 'primary',
    autocomplete = 'on',
    autofocus = null,
    dataTestId = '',
    min = null,
    disableScrollChange = true,
    class: className = '',
    onchange = undefined,
    onclick = undefined,
    onfocus = undefined,
    oninput = undefined,
    onblur = undefined,
    onkeyup = undefined,
    onsearch = undefined,
  }: Props = $props();

  // One bag of forwarded DOM handlers. The same five directives were repeated
  // verbatim on all ten <input> type branches.
  const forwarded = $derived({ onchange, onclick, onfocus, oninput, onblur });

  const searchForwarded = $derived({ ...forwarded, onkeyup, onsearch });

  const iconString = $derived(
    typeof icon === 'string'
      ? icon
      : {
          password: 'key',
          email: 'alternate_email',
          date: 'calendar',
          number: 'hashtag',
          tel: 'phone',
          url: 'link',
          search: 'search',
          range: 'sliders',
          file: 'paperclip',
          time: 'clock',
          'datetime-local': 'calendar',
          datetime: 'calendar',
          hidden: 'eye-off',
          text: 'message-circle',
        }[type ?? 'text'],
  );

  let input: HTMLInputElement | undefined = $state();

  export function focus() {
    input?.focus();
  }

  const variants = $derived(
    {
      primary: 'bg-transparent text-gray-2 border-gray-6 placeholder-gray-6',
      transparent: 'text-white bg-transparent border-0 col-span-3',
    }[variant],
  );

  const classes = $derived(
    twMerge([
      'h-8 px-2 leading-4 block border w-full rounded-sm focus:border-white outline-none dark:[color-scheme:dark] transition',
      variants,
      // sizes,
      disabled &&
        // 'bg-gray-9 border-gray-650 hover:border-gray-650 dark:hover:border-gray-650 dark:text-gray-100',
        'bg-gray-9 text-gray-3 border-gray-9 hover:border-gray-9',
      icon && 'pl-6',
      type === 'file' && 'p-2 h-10',
      !valid &&
        'border-error hover:border-error dark:border-error dark:hover:border-error animate-shake',
      className,
    ]),
  );
</script>

<div class="w-full">
  {#if label}
    <div class="mb-1 font-mono dark:text-white">{label}</div>
  {/if}

  <div class="relative flex w-full">
    <div class="absolute left-2 flex h-full items-center text-gray-4">
      {#if icon}
        <Icon icon={iconString} size="md" />
      {/if}
    </div>

    <!-- Because "type" can't be dynamic, we have to repeat for every "type" -->
    {#if type === 'password'}
      <input
        type="password"
        {id}
        {name}
        {autocomplete}
        class={classes}
        {placeholder}
        {disabled}
        {autofocus}
        bind:this={input}
        bind:value
        {...forwarded}
        data-testid={dataTestId}
      />
    {:else if type === 'email'}
      <input
        type="email"
        {id}
        {name}
        {autocomplete}
        class={classes}
        {placeholder}
        {disabled}
        {autofocus}
        bind:this={input}
        bind:value
        {...forwarded}
        data-testid={dataTestId}
      />
    {:else if type === 'datetime-local'}
      <input
        type="datetime-local"
        {id}
        {name}
        {autocomplete}
        class={classes}
        {placeholder}
        {disabled}
        {autofocus}
        bind:this={input}
        bind:value
        {...forwarded}
        data-testid={dataTestId}
      />
    {:else if type === 'number'}
      <input
        type="number"
        {id}
        {name}
        {autocomplete}
        class={classes}
        {placeholder}
        {disabled}
        {autofocus}
        bind:this={input}
        bind:value
        {...forwarded}
        onwheel={disableScrollChange ? () => input?.blur() : null}
        data-testid={dataTestId}
        {min}
      />
    {:else if type === 'time'}
      <input
        type="time"
        {id}
        {name}
        {autocomplete}
        class={classes}
        {placeholder}
        {disabled}
        {autofocus}
        bind:this={input}
        bind:value
        {...forwarded}
        data-testid={dataTestId}
      />
    {:else if type === 'date'}
      <input
        type="date"
        {id}
        {name}
        {autocomplete}
        class={classes}
        {placeholder}
        {disabled}
        {autofocus}
        bind:this={input}
        bind:value
        {...forwarded}
        data-testid={dataTestId}
      />
    {:else if type === 'datetime'}
      <input
        type="datetime-local"
        {id}
        {name}
        {autocomplete}
        class={classes}
        {placeholder}
        {disabled}
        {autofocus}
        bind:this={input}
        bind:value
        {...forwarded}
        data-testid={dataTestId}
      />
    {:else if type === 'file'}
      <input
        type="file"
        {id}
        {name}
        {autocomplete}
        class={classes}
        {placeholder}
        {disabled}
        {accept}
        bind:this={input}
        bind:value
        {...forwarded}
        data-testid={dataTestId}
      />
    {:else if type === 'search'}
      <input
        type="search"
        {id}
        {name}
        {autocomplete}
        class={classes}
        {placeholder}
        {disabled}
        {accept}
        bind:this={input}
        bind:value
        {...searchForwarded}
        data-testid={dataTestId}
      />
    {:else}
      <input
        type="text"
        {id}
        {name}
        {autocomplete}
        class={classes}
        {placeholder}
        {disabled}
        {autofocus}
        bind:this={input}
        bind:value
        {...forwarded}
        data-testid={dataTestId}
      />
    {/if}
    {#if loading}
      <div class="absolute right-0 h-full p-3">
        <Spinner size="xs" />
      </div>
    {/if}
  </div>
</div>
